import { useRequestStatus } from '@/shared/lib';
import { changePassword } from '../api';
import type {
  ChangePasswordFormValues,
  ChangePasswordRequestDto,
  MfaRequiredResponseData,
} from '../model';
import { useSessionStore } from '@/entities/session';
import { isVerificationCodeInvalidError } from '@/shared/utils';
import { MFA_REQUIRED_RESPONSE_CODE } from '@/shared/config';

export const useChangePasswordFlow = ({
  onMfaRequired,
  onLogout,
  onSuccess,
  resetCode,
}: {
  onMfaRequired: (data: MfaRequiredResponseData) => void;
  onLogout: () => void;
  onSuccess: () => void;
  resetCode: () => void;
}) => {
  const { session, setSession } = useSessionStore();
  const { isLoading, setIsLoading, error, setError } = useRequestStatus();

  const handleChangePassword = async (data: ChangePasswordFormValues) => {
    const requestData: ChangePasswordRequestDto = {
      newPassword: data.password,
      twoFA: data.twoFA.code ? data.twoFA : null,
    };

    try {
      setIsLoading(true);
      const response = await changePassword(requestData, {
        throwOnError: false,
      });

      if ('accessToken' in response) {
        setSession({ ...session, ...response });
        setError(null);
        onSuccess();
        return;
      }

      if ('code' in response) {
        if (isVerificationCodeInvalidError(response)) {
          resetCode();
        }

        if (response.code === MFA_REQUIRED_RESPONSE_CODE && response.data) {
          onMfaRequired(response.data);
        } else if (response.code === 'NEED_PASSWORD_LOGIN') {
          onLogout();
        } else {
          setError(response.message);
        }
      } else {
        throw new Error('Unexpected server response');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleChangePassword,
    isLoading,
    error,
    setError,
  };
};
